import { CalendarPlus, Check, Hand, MapPin, PackageCheck, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MATERIAL_ESTADOS, formatReservaRango, formatVuelve } from "@/lib/material";
import { MaterialEstadoBadge, MaterialFoto, MaterialUser } from "@/components/material/MaterialShared";

export default function MaterialItemCard({
  item,
  me,
  usersByEmail,
  selected,
  onToggleSelect,
  onOpen,
  onReservar,
  onCogerAhora,
  onRecoger,
  onDevolver,
}) {
  const cfg = MATERIAL_ESTADOS[item.estado] || MATERIAL_ESTADOS.disponible;
  const actual = item.actual;
  const esMio = actual?.user_email === me;
  const loTengoYo = esMio && actual?.estado === "en_uso";
  const puedoRecoger = esMio && actual?.estado === "reservada";

  return (
    <div
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all hover:shadow-md",
        selected && "ring-2 ring-primary",
        item.estado === "retrasado" && !selected && "ring-2 ring-red-400/60"
      )}
    >
      <button type="button" onClick={() => onOpen(item)} className="relative block text-left">
        <MaterialFoto item={item} className="aspect-[4/3] w-full" iconClassName="w-10 h-10" />
        <div className="absolute left-2 top-2">
          <MaterialEstadoBadge estado={item.estado} className="bg-white/95 shadow-sm backdrop-blur" />
        </div>
      </button>
      <button
        type="button"
        aria-label={selected ? "Quitar de la selección" : "Seleccionar para reservar varios"}
        onClick={() => onToggleSelect(item.id)}
        className={cn(
          "absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border-2 shadow-sm transition-all",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-white bg-black/20 text-transparent opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-black/40"
        )}
      >
        <Check className="h-4 w-4" strokeWidth={3} />
      </button>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <button type="button" onClick={() => onOpen(item)} className="text-left">
          <h3 className="font-semibold leading-tight line-clamp-2">{item.nombre}</h3>
          {item.ubicacion && (
            <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">{item.ubicacion}</span>
            </p>
          )}
        </button>

        <div className={cn("rounded-lg px-2.5 py-2 text-xs min-h-[3.25rem] flex flex-col justify-center", actual ? "bg-muted/60" : "bg-transparent")}>
          {actual ? (
            <>
              <MaterialUser email={actual.user_email} usersByEmail={usersByEmail} me={me} size={18} nameClassName="font-medium text-foreground" />
              <span className={cn("mt-0.5", item.estado === "retrasado" ? "font-semibold text-red-600" : "text-muted-foreground")}>
                {actual.estado === "en_uso" ? formatVuelve(actual) : `reservado · ${formatReservaRango(actual)}`}
              </span>
            </>
          ) : item.estado === "reparacion" ? (
            <span className="text-muted-foreground">No disponible hasta que se repare</span>
          ) : item.proxima ? (
            <span className="text-muted-foreground">
              Próxima reserva: <span className="font-medium text-foreground">{formatReservaRango(item.proxima)}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Sin reservas próximas</span>
          )}
        </div>

        <div className="mt-auto flex gap-2 pt-1">
          {loTengoYo ? (
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => onDevolver(actual, item)}>
              <Undo2 className="h-4 w-4" /> Devolver
            </Button>
          ) : puedoRecoger ? (
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => onRecoger(actual)}>
              <PackageCheck className="h-4 w-4" /> Recoger
            </Button>
          ) : item.estado === "disponible" ? (
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => onCogerAhora(item)}>
              <Hand className="h-4 w-4" /> Coger ahora
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            className={cn("gap-1.5", !(loTengoYo || puedoRecoger || item.estado === "disponible") && "flex-1")}
            onClick={() => onReservar([item.id])}
            title="Reservar para otras fechas"
          >
            <CalendarPlus className="h-4 w-4" /> Reservar
          </Button>
        </div>
      </div>
      <span className={cn("absolute inset-x-0 bottom-0 h-1", cfg.dot)} aria-hidden />
    </div>
  );
}
